import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { AuthGoogleAdsDialog } from "./AuthGoogleAdsDialog";
import { useAddGoogleAdsAccounts, useGetGoogleAdsStatus } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { PermissionAlertDialog } from "components";

export function BrowseGoogleAdsCard({ userProfile }) {
  const navigate = useNavigate();
  const [googleAdsConfigs, setGoogleAdsConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const addGoogleAdsAccounts = useAddGoogleAdsAccounts();
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);

  const { data: googleAdsStatusData, isLoading: isStatusLoading } = useGetGoogleAdsStatus(
    userProfile.organisation_id
  );

  const { setToastMessage } = useToast();

  const isGoogleAdsActive = () => {
    if (isStatusLoading) return false;
    return googleAdsStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      const result = await addGoogleAdsAccounts.mutateAsync({
        organisation_id: userProfile.organisation_id,
        configs: googleAdsConfigs.map(({ googleAdsAccountId }) => ({
          google_ads_account_id: googleAdsAccountId,
        })),
      });
      if (result?.data?.status === "success") {
        navigate("/google-ads-setup", {
          state: {
            configs: googleAdsConfigs.map(({ googleAdsAccountId }) => ({
              google_ads_account_id: googleAdsAccountId,
            })),
          }, 
        });
      } else {
        setToastMessage("Error connecting Google Ads. Please try again or email support.");
      }
    } catch (error) {
      setToastMessage("Error connecting Google Ads. Please try again or email support.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";
    
    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  return (
    <>
      <Card style={{ display: "flex", width: "100%", padding: "var(--space-4)", justifyContent: "space-between",
          alignItems: "center" }}>
        <Flex align="center" gap="4">
          {/* Google ads logo */}
          <Flex height="40px" width="40px" p="2" justify="center" align="center"
            style={{ borderRadius: "var(--space-1)", background: "#FFFFFF", border: "1px solid var(--gray-a6)" }}>
            <img src={require("../../../assets/images/google.png")} alt="Google Ads" style={{ width: "24px" }}/>
          </Flex>
          {/* Heading and subtext */}
          <Flex gap="3" align="start">
            <Flex direction="column" justify="center" align="start">
              <Text size="4" weight="medium">Google Ads</Text>
              <Text color="gray" size="1">Advertising platform</Text>
            </Flex>
            <Badge size="3" color="amber">Paid feature</Badge>
          </Flex>
        </Flex>

        <Flex align="center">
          {isGoogleAdsActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      {/* ✅ Updated props to match new dialog interface */}
      <AuthGoogleAdsDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onContinue={handleContinue}
        googleAdsConfigs={googleAdsConfigs}
        setGoogleAdsConfigs={setGoogleAdsConfigs}
        isLoading={isLoading}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
